<script lang="ts">
    import { onMount } from 'svelte';
    import type { Event, CalendarUrls, EventShareUrls } from '../../api/events';
    import {
        formatEventScheduleDisplay,
        generateCalendarUrls,
        getEventShareUrls,
        getEventUserUrlToken,
        resolveEventId
    } from '../../api/events';
    import { associationIdName, getQueryParam, vvSettings } from '../../api/common';
    import * as t from '../../translations/Events.ts';
    import FacebookIcon from '../../icons/FacebookIcon.svelte';
    import TwitterIcon from '../../icons/TwitterIcon.svelte';
    import LinkedInIcon from '../../icons/LinkedInIcon.svelte';
    import EmailIcon from '../../icons/EmailIcon.svelte';
    import CkEditorContent from '../CkEditorContent.svelte';
    
    export let event: Event;
    /** Hash-route event id (`#/Confirmation/:id`) -- fallback when API returns `id: 0`. */
    export let eventRouteId: number | undefined = undefined;
    export let thankYouMessage: string = '';
    
    let calendarUrls: CalendarUrls;
    $: calendarUrls = generateCalendarUrls(event);
    let isCalendarOpen = false;
    let calendarContainer: HTMLDivElement;
    let copied = false;

    // Tracked share URLs come from the legacy UserSite ShareUrls endpoint
    // (mirrors Petitions). Each URL contains a per-share token so Action
    // Center reports can attribute traffic to a specific user + medium.
    // Falls back to the locally-built generic URL if the endpoint is
    // unreachable (offline, dev environments without the endpoint deployed,
    // share-tracking disabled, etc.).
    let trackedShareUrls: EventShareUrls | null = null;

    /**
     * Use the live origin in production. On localhost the registration page only exists
     * on real dev/prod so a shareable link should point there instead of the dev server.
     */
    function getShareBaseUrl(): string {
        const origin = window.location.origin;
        if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
            return 'https://dev.votervoice.net';
        }
        return origin;
    }

    $: resolvedEventId = resolveEventId(event, eventRouteId);

    // Fallback / generic URL (used until the tracked URLs load OR if the
    // tracked-URLs request fails). Once loaded, `webShareUrl` from the
    // backend takes precedence for the copy-link box.
    $: fallbackShareUrl = resolvedEventId
        ? `${getShareBaseUrl()}/${associationIdName}/Events/${resolvedEventId}/Register`
        : '';
    $: shareUrl = trackedShareUrls?.webShareUrl ?? fallbackShareUrl;
    $: facebookHref = trackedShareUrls?.facebookShareUrl ?? shareUrl;
    $: twitterHref = trackedShareUrls?.twitterShareUrl ?? shareUrl;
    $: linkedInHref = trackedShareUrls?.linkedInShareUrl ?? shareUrl;
    $: shareTitle = event.title || 'this event';
    $: scheduleDisplay = formatEventScheduleDisplay(event);

    function downloadICS() {
        const link = document.createElement('a');
        link.href = calendarUrls.apple;
        link.download = `${event.title}.ics`;
        link.click();
    }

    function toggleCalendarMenu() {
        isCalendarOpen = !isCalendarOpen;
    }

    function copyToClipboard() {
        navigator.clipboard.writeText(shareUrl);
        copied = true;
        setTimeout(() => (copied = false), 2000);
    }

    function openShareInNewTab(url: string) {
        const popup = window.open(url, '_blank');
        if (popup) popup.opener = null;
    }

    function shareToFacebook() {
        const url = facebookHref;
        if (!url) return;
        // Facebook's sharer reads the OG metadata from the URL the user posts,
        // so we want the URL itself to be the tracked one (not a wrapping
        // facebook share URL containing the tracked link as a query param).
        openShareInNewTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
    }

    function shareToTwitter() {
        const url = twitterHref;
        if (!url) return;
        const text = `I just registered for ${shareTitle}. Join me!`;
        openShareInNewTab(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
        );
    }

    function shareToLinkedIn() {
        const url = linkedInHref;
        if (!url) return;
        openShareInNewTab(
            `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(shareTitle)}&summary=`
        );
    }

    function shareViaEmail() {
        // Email uses the web (tracked) URL by default so click-throughs from
        // forwarded emails still attribute to the originator.
        const body = `I just registered for ${shareTitle}.\n\n${shareUrl}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(body)}`;
    }

    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    function getEventsListUrl(): string {
        return `/${associationIdName}${getSiteSegment()}/Events`;
    }

    function handleBack() {
        window.location.href = getEventsListUrl();
    }

    function handleViewRegistrations() {
        sessionStorage.setItem('eventsActiveTab', 'registered');
        window.location.href = getEventsListUrl();
    }

    /**
     * Fetch tracked share URLs from the legacy /Api/Events/{id}/ShareUrls
     * endpoint (one share record per medium, attributed to the current
     * remembered user if one is available). Anonymous calls still produce
     * trackable URLs; only the originator attribution is missing.
     *
     * Falls back silently to the generic url if the request fails so the page
     * still renders a usable copy-link / share buttons in dev environments
     * that haven't deployed the endpoint yet.
     *
     * Only runs when sharing is enabled for this event -- no point creating
     * share records for a page that doesn't show the share section.
     */
    async function loadTrackedShareUrls(eventId: number) {
        if (event.sharingEnabled === false) return;

        const userToken = getEventUserUrlToken();
        // `vvsn` (referring nugget) propagates the upstream share that brought
        // the user here -- if they originally arrived via a tracked share URL,
        // preserve the chain in our new share records.
        const referringNugget = getQueryParam('vvsn') ?? undefined;

        try {
            trackedShareUrls = await getEventShareUrls(eventId, userToken, referringNugget);
        } catch (e) {
            // Reporting endpoint is best-effort -- never block the
            // confirmation page on it.
            console.warn('Tracked share URLs unavailable; falling back to generic link.', e);
        }
    }

    onMount(() => {
        const onDocumentClick = (evt: MouseEvent) => {
            if (calendarContainer && !calendarContainer.contains(evt.target as Node)) {
                isCalendarOpen = false;
            }
        };
        document.addEventListener('click', onDocumentClick);

        const eventId = resolveEventId(event, eventRouteId);
        if (eventId) {
            void loadTrackedShareUrls(eventId);
        }

        return () => document.removeEventListener('click', onDocumentClick);
    });
</script>

<div class="confirmation-shell max-w-3xl mx-auto px-4 md:px-6 py-8 flex flex-col items-center text-center" data-testid="event-confirmation">
    <!-- Success Animation (mirrors petitions ActionConfirmation) -->
    <div class="relative mb-6" data-testid="confirmation-success-icon">
        <div
            class="w-24 h-24 rounded-full flex items-center justify-center shadow-lg animate-success-pop"
            style="background: linear-gradient(to bottom right, var(--vv-action-center-button-color, #0d9488), color-mix(in srgb, var(--vv-action-center-button-color, #0d9488) 75%, #ffffff));"
        >
            <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
            </svg>
        </div>
    </div>

    <!--
        Title only. The personalized thank-you copy renders in its own card below
        (see "Custom thank-you message" block). The REST backend's
        ConfirmationDisplaySettings.ThankYouMessage is always populated -- it
        defaults to a translated "Thank you for registering for this event!"
        (Domains/VoterVoice.Events/DisplaySettings/ConfirmationDisplaySettings)
        when the admin hasn't customized it, and contains the admin's rich-HTML
        copy when they have. Rendering a separate generic subtitle here would
        duplicate that message verbatim for un-customised events and dilute the
        admin's message for customised ones. Legacy MVC ConfirmRegistration also
        shows only the per-event ThankYouMessage.
    -->
    <h1 class="!text-3xl md:!text-4xl !leading-tight !font-bold !text-gray-900 mb-8" data-testid="confirmation-title">
        {t.Registration_Confirmed()}
    </h1>

    <!-- Event Summary Card (centered like thank-you + share blocks on this page) -->
    <div class="w-full max-w-xl bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8 text-center" data-testid="event-summary">
        <h2 class="!text-lg !leading-7 !font-semibold !text-gray-900 mb-3">{event.title}</h2>
        <div class="space-y-2 !text-sm !leading-5 !text-gray-600">
            {#if scheduleDisplay}
                <div class="flex items-center justify-center gap-2">
                    <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{scheduleDisplay}</span>
                </div>
            {/if}
            {#if event.location}
                <div class="flex items-center justify-center gap-2">
                    <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{event.location}</span>
                </div>
            {/if}
        </div>
    </div>

    <!--
        Custom thank-you message (rendered as HTML like the legacy MVC `@Html.Raw(Model.ThankYouMessage)`).
        Sourced in this order by ConfirmationPage: event.thankYouMessage (admin-configured per-event)
        → store.thankYouMessage (registration POST response message). Card is hidden when neither is set.
    -->
    {#if thankYouMessage}
        <div class="w-full max-w-xl bg-white border border-gray-200 rounded-xl p-5 mb-8 text-center" data-testid="thank-you-message">
            <CkEditorContent
                html={thankYouMessage}
                className="text-sm text-gray-700 mx-auto confirmation-thankyou-content"
            />
        </div>
    {/if}

    <!-- Add to Calendar (label on trigger only — legacy ConfirmRegistration uses "Set Reminder" header + button text, not duplicate titles) -->
    <div class="mb-8" data-testid="add-to-calendar">
        <div class="calendar-mock" bind:this={calendarContainer}>
            <button
                type="button"
                class="calendar-mock__trigger"
                on:click|stopPropagation={toggleCalendarMenu}
                aria-expanded={isCalendarOpen}
                aria-label={t.Add_To_Calendar()}
            >
                <span class="calendar-mock__trigger-label">
                    <span class="calendar-mock__icon calendar-mock__icon--trigger" aria-hidden="true">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </span>
                    <span>{t.Add_To_Calendar()}</span>
                </span>
                <svg class="calendar-mock__chevron" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
            </button>

            {#if isCalendarOpen}
                <div class="calendar-mock__menu">
                    <a href={calendarUrls.google} target="_blank" rel="noopener noreferrer" class="calendar-mock__item">
                        <span class="calendar-mock__icon calendar-mock__icon--google" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                                <path d="M564 325.8C564 467.3 467.1 568 324 568C186.8 568 76 457.2 76 320C76 182.8 186.8 72 324 72C390.8 72 447 96.5 490.3 136.9L422.8 201.8C334.5 116.6 170.3 180.6 170.3 320C170.3 406.5 239.4 476.6 324 476.6C422.2 476.6 459 406.2 464.8 369.7L324 369.7L324 284.4L560.1 284.4C562.4 297.1 564 309.3 564 325.8z" />
                            </svg>
                        </span>
                        <span>{t.Google_Calendar()}</span>
                    </a>
                    <a href={calendarUrls.outlook} target="_blank" rel="noopener noreferrer" class="calendar-mock__item">
                        <span class="calendar-mock__icon calendar-mock__icon--outlook" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                                <path d="M96 96L310.6 96L310.6 310.6L96 310.6L96 96zM329.4 96L544 96L544 310.6L329.4 310.6L329.4 96zM96 329.4L310.6 329.4L310.6 544L96 544L96 329.4zM329.4 329.4L544 329.4L544 544L329.4 544L329.4 329.4z" />
                            </svg>
                        </span>
                        <span>{t.Outlook_Calendar()}</span>
                    </a>
                    <button type="button" class="calendar-mock__item calendar-mock__item--button" on:click={downloadICS}>
                        <span class="calendar-mock__icon calendar-mock__icon--apple" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                                <path d="M447.1 332.7C446.9 296 463.5 268.3 497.1 247.9C478.3 221 449.9 206.2 412.4 203.3C376.9 200.5 338.1 224 323.9 224C308.9 224 274.5 204.3 247.5 204.3C191.7 205.2 132.4 248.8 132.4 337.5C132.4 363.7 137.2 390.8 146.8 418.7C159.6 455.4 205.8 545.4 254 543.9C279.2 543.3 297 526 329.8 526C361.6 526 378.1 543.9 406.2 543.9C454.8 543.2 496.6 461.4 508.8 424.6C443.6 393.9 447.1 334.6 447.1 332.7zM390.5 168.5C417.8 136.1 415.3 106.6 414.5 96C390.4 97.4 362.5 112.4 346.6 130.9C329.1 150.7 318.8 175.2 321 202.8C347.1 204.8 370.9 191.4 390.5 168.5z" />
                            </svg>
                        </span>
                        <span>{t.Apple_Calendar()}</span>
                    </button>
                    <a href={calendarUrls.yahoo} target="_blank" rel="noopener noreferrer" class="calendar-mock__item">
                        <span class="calendar-mock__icon calendar-mock__icon--yahoo" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                                <path d="M287.8 205.1L231.1 348.3L175.1 205.1L79 205.1L184.8 454.2L146.2 544L240.4 544L381.3 205.1L287.7 205.1zM393.2 340.9C361.1 340.9 335 367 335 399.1C335 431.2 361.1 457.3 393.2 457.3C425.3 457.3 451.4 431.2 451.4 399.1C451.4 367 425.3 340.9 393.2 340.9zM458.7 96L365.7 319.5L470.5 319.5L563.1 96L458.7 96z" />
                            </svg>
                        </span>
                        <span>{t.Yahoo_Calendar()}</span>
                    </a>
                </div>
            {/if}
        </div>
    </div>

    <!--
        Share Section.
        Gated by event.sharingEnabled. The REST backend sets this to the
        combined ConfirmationDisplaySettings.ShowTellAFriendLink (per-event
        Event.ShowTellAFriendLink AND the association-level default). When the
        admin has disabled "Enable Sharing" on this event we render nothing
        here -- no "Sharing is disabled" banner, just hide the entire block.

        We treat `undefined` as enabled so legacy backends / mock fixtures that
        haven't plumbed the flag yet keep showing the share section instead of
        silently hiding it. Only an explicit `false` from the REST backend
        suppresses the section.
    -->
    {#if event.sharingEnabled !== false}
    <div class="w-full max-w-xl" data-testid="share-section">
        <h2 class="!text-lg !leading-7 !font-semibold !text-gray-900 mb-4">{t.Share_With_Friends_Header()}</h2>

        <div class="grid grid-cols-4 gap-3 mb-4" data-testid="share-buttons">
            <button
                class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-[#1877F2] !text-white rounded-xl hover:opacity-90 transition-opacity"
                on:click={shareToFacebook}
                aria-label="Share on Facebook"
                data-testid="share-facebook"
            >
                <FacebookIcon width="24" height="24" />
                <span class="!text-xs !font-medium !leading-4">Facebook</span>
            </button>

            <button
                class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-black !text-white rounded-xl hover:opacity-90 transition-opacity"
                on:click={shareToTwitter}
                aria-label="Share on X"
                data-testid="share-twitter"
            >
                <TwitterIcon width="24" height="24" />
                <span class="!text-xs !font-medium !leading-4">X</span>
            </button>

            <button
                class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-[#0A66C2] !text-white rounded-xl hover:opacity-90 transition-opacity"
                on:click={shareToLinkedIn}
                aria-label="Share on LinkedIn"
                data-testid="share-linkedin"
            >
                <LinkedInIcon width="24" height="24" />
                <span class="!text-xs !font-medium !leading-4">LinkedIn</span>
            </button>

            <button
                class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-gray-600 !text-white rounded-xl hover:opacity-90 transition-opacity"
                on:click={shareViaEmail}
                aria-label="Share via Email"
                data-testid="share-email"
            >
                <EmailIcon width="24" height="24" />
                <span class="!text-xs !font-medium !leading-4">Email</span>
            </button>
        </div>

        <p class="!text-sm !leading-5 !text-gray-500 mb-3 text-center">
            {t.Personal_Website_Share_Instructions()}
        </p>
        <div class="copy-link-shell flex items-center gap-2 bg-gray-100 rounded-lg p-2" data-testid="share-copy-link">
            <input
                type="text"
                value={shareUrl}
                readonly
                class="copy-link-input flex-1 min-w-0 bg-transparent !text-base !leading-6 !text-gray-600 px-2 outline-none truncate"
                data-testid="share-url-input"
            />
            <button
                class="copy-link-button inline-flex items-center justify-center px-4 py-2 !min-h-[42px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !font-medium !text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                on:click={copyToClipboard}
                data-testid="copy-link-button"
            >
                {copied ? `✓ ${t.Copied_Label()}` : t.Copy_Link_Button()}
            </button>
        </div>
    </div>
    {/if}

    <!-- Navigation Buttons (uses confirmation-nav-button styling like petitions) -->
    <div class="flex flex-col sm:flex-row gap-3 mt-8 w-full max-w-xl" data-testid="navigation-buttons">
        <button
            type="button"
            class="confirmation-nav-button flex-1 px-6 py-3 !min-h-[48px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !text-gray-700 !font-medium hover:bg-gray-50 transition-colors"
            on:click={handleBack}
            data-testid="back-to-events-button"
        >
            ← {t.Back_To_Events()}
        </button>
        <button
            type="button"
            class="confirmation-nav-button flex-1 px-6 py-3 !min-h-[48px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !text-gray-700 !font-medium hover:bg-gray-50 transition-colors"
            on:click={handleViewRegistrations}
            data-testid="view-my-registrations-button"
        >
            {t.View_My_Registrations()} →
        </button>
    </div>
</div>


<style>
    @keyframes success-pop {
        0% {
            transform: scale(0);
            opacity: 0;
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
    .animate-success-pop {
        animation: success-pop 0.5s ease-out forwards;
    }

    /* Prevent host page CSS from inflating fonts/controls in embedded contexts (parity with petitions). */
    :global(.confirmation-shell button) {
        font-family: inherit;
    }

    :global(.copy-link-shell) {
        background-color: #f3f4f6 !important;
        border-radius: 0.5rem !important;
    }

    :global(.copy-link-input) {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        outline: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.copy-link-input:focus),
    :global(.copy-link-input:active),
    :global(.copy-link-input[readonly]) {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
    }

    :global(.copy-link-button) {
        background-color: #ffffff !important;
        border: 1px solid #d1d5db !important;
        border-radius: 0.5rem !important;
        color: #374151 !important;
        box-shadow: none !important;
        min-width: 7.5rem !important;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
        flex-shrink: 0 !important;
        box-sizing: border-box !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.copy-link-button:hover) {
        background-color: #f9fafb !important;
        border-color: #d1d5db !important;
    }

    /*
     * Reset host-page <button>/<a> styling so the bottom nav controls render as the
     * intended pill buttons (the previous "Back to Events" looked like an underlined
     * link because the host CSS bled through).
     */
    :global(.confirmation-nav-button) {
        background-color: #ffffff !important;
        border: 1px solid #d1d5db !important;
        border-radius: 0.5rem !important;
        color: #374151 !important;
        text-decoration: none !important;
        box-shadow: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.confirmation-nav-button:hover) {
        background-color: #f9fafb !important;
        border-color: #d1d5db !important;
        text-decoration: none !important;
    }

    /* Match petitions: drop the rich-text first/last margins so the message hugs the card edges. */
    :global(.confirmation-thankyou-content > :first-child) {
        margin-top: 0;
    }
    :global(.confirmation-thankyou-content > :last-child) {
        margin-bottom: 0;
    }

    /* Calendar dropdown (events-specific). */
    .calendar-mock {
        position: relative;
        display: inline-block;
        min-width: 200px;
    }

    .calendar-mock__trigger {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border: 1px solid #d0d5dd;
        border-radius: 10px;
        background: #fff;
        color: #111827;
        font-weight: 600;
        padding: 10px 10px 10px 12px;
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
    }

    .calendar-mock__trigger-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .calendar-mock__chevron {
        width: 16px;
        height: 16px;
        color: #667085;
    }

    .calendar-mock__menu {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        right: 0;
        z-index: 20;
        border: 1px solid #eaecf0;
        border-radius: 10px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 10px 30px rgba(16, 24, 40, 0.15);
    }

    .calendar-mock__item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: #fff;
        color: #344054;
        text-decoration: none;
        font-size: 14px;
        border: none;
        border-bottom: 1px solid #f2f4f7;
        cursor: pointer;
    }

    .calendar-mock__item:last-child {
        border-bottom: 0;
    }

    .calendar-mock__item:hover {
        background: #f9fafb;
    }

    .calendar-mock__item--button {
        font-family: inherit;
        text-align: left;
    }

    .calendar-mock__item .calendar-mock__icon {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .calendar-mock__icon svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    /* Trigger uses the same outline calendar as event cards / date row */
    .calendar-mock__icon--trigger {
        width: auto;
        height: auto;
        border-radius: 0;
        color: #667085;
    }

    .calendar-mock__icon--trigger svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
    }

    .calendar-mock__icon--google {
        color: #2d9e4a;
    }

    .calendar-mock__icon--outlook {
        color: #0078d4;
    }

    .calendar-mock__icon--apple {
        color: #111827;
    }

    .calendar-mock__icon--yahoo {
        color: #6001d2;
    }
</style>
